import { type RefObject, useEffect, useRef, useState } from 'react';

const SCROLL_DIRECTION_THRESHOLD_PX = 4;
const PIN_ENTER_OFFSET_PX = 8;
const TOP_UNPIN_PX = 1;
/** Ignore upward deltas in this zone — iOS rubber-band bounce looks like scroll-up. */
const BOTTOM_BOUNCE_GUARD_PX = 72;

export type ScrollPinnedHeaderState = {
  pinned: boolean;
  revealed: boolean;
  /** Transitions only after the first hidden pin paint — avoids enter flicker. */
  animate: boolean;
};

function isNearScrollBottom(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
): boolean {
  const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
  // Overscroll past the end (rubber-band) or within the guard of the real bottom.
  return (
    scrollTop > maxScrollTop ||
    scrollTop >= maxScrollTop - BOTTOM_BOUNCE_GUARD_PX
  );
}

/**
 * Single-header scroll behavior for native: the in-flow header scrolls away
 * naturally, then the same element pins fixed (hidden) until the user scrolls up.
 */
export function useScrollPinnedHeader(
  headerRef: RefObject<HTMLElement | null>,
  headerHeight: number,
  enabled: boolean,
): ScrollPinnedHeaderState {
  const [pinned, setPinned] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [animate, setAnimate] = useState(false);
  const pinnedRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const enableAnimateRafRef = useRef<number | null>(null);

  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  useEffect(() => {
    if (!enabled) {
      pinnedRef.current = false;
      return;
    }

    let scrollEl: HTMLElement | null = null;
    let cancelled = false;

    const cancelEnableAnimate = () => {
      if (enableAnimateRafRef.current != null) {
        cancelAnimationFrame(enableAnimateRafRef.current);
        enableAnimateRafRef.current = null;
      }
    };

    const unpinImmediately = () => {
      cancelEnableAnimate();
      pinnedRef.current = false;
      setPinned(false);
      setRevealed(false);
      setAnimate(false);
    };

    /**
     * Pin starts hidden. Enable transitions only after that hidden state has
     * painted — otherwise CSS interpolates from the in-flow visible header
     * and the sticky bar flickers in for a frame.
     */
    const pinHidden = () => {
      cancelEnableAnimate();
      pinnedRef.current = true;
      setPinned(true);
      setRevealed(false);
      setAnimate(false);

      enableAnimateRafRef.current = requestAnimationFrame(() => {
        enableAnimateRafRef.current = requestAnimationFrame(() => {
          enableAnimateRafRef.current = null;
          if (pinnedRef.current && !cancelled) {
            setAnimate(true);
          }
        });
      });
    };

    const applyScroll = () => {
      if (!scrollEl) {
        return;
      }

      const scrollTop = scrollEl.scrollTop;
      const delta = scrollTop - lastScrollTopRef.current;
      const { scrollHeight, clientHeight } = scrollEl;

      if (headerHeight <= 0) {
        unpinImmediately();
        lastScrollTopRef.current = scrollTop;
        return;
      }

      if (scrollTop <= TOP_UNPIN_PX) {
        unpinImmediately();
        lastScrollTopRef.current = scrollTop;
        return;
      }

      const pinEnterAt = headerHeight + PIN_ENTER_OFFSET_PX;

      if (!pinnedRef.current) {
        if (scrollTop >= pinEnterAt) {
          pinHidden();
        }

        lastScrollTopRef.current = scrollTop;
        return;
      }

      if (delta < -SCROLL_DIRECTION_THRESHOLD_PX) {
        const bounceRecovery =
          isNearScrollBottom(scrollTop, scrollHeight, clientHeight) ||
          isNearScrollBottom(
            lastScrollTopRef.current,
            scrollHeight,
            clientHeight,
          );

        if (!bounceRecovery) {
          setRevealed(true);
        }
      } else if (delta > SCROLL_DIRECTION_THRESHOLD_PX) {
        setRevealed(false);
      }

      lastScrollTopRef.current = scrollTop;
    };

    const onScroll = () => {
      if (rafRef.current != null) {
        return;
      }

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        applyScroll();
      });
    };

    void (async () => {
      const headerEl = headerRef.current;
      const ionContent = headerEl?.closest('ion-content');
      if (!headerEl || !ionContent || cancelled) {
        return;
      }

      scrollEl = await (ionContent as HTMLIonContentElement).getScrollElement();
      if (cancelled) {
        return;
      }

      lastScrollTopRef.current = scrollEl.scrollTop;
      applyScroll();
      scrollEl.addEventListener('scroll', onScroll, { passive: true });
    })();

    return () => {
      cancelled = true;
      scrollEl?.removeEventListener('scroll', onScroll);
      cancelEnableAnimate();

      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      pinnedRef.current = false;
      setPinned(false);
      setRevealed(false);
      setAnimate(false);
    };
  }, [enabled, headerHeight, headerRef]);

  return {
    pinned: enabled ? pinned : false,
    revealed: enabled ? revealed : false,
    animate: enabled ? animate : false,
  };
}
